import PptxGenJS from "pptxgenjs";
import { AspectRatios } from "../../../entities/AspectRatios";
import { PreviewSlide, SlideLine } from "../editor/PreviewSlide";

export class PresentationExporter {
	static fontSizes = {
		"4x3": {
			small: 22,
			medium: 41,
			large: 60,
		},
		"16x9": {
			small: 16,
			medium: 34,
			large: 52,
		},
		size: {
			large: 55,
			medium: 200,
		},
	};

	public static exportWithImagePath(slides: PreviewSlide[], aspectRatio: AspectRatios, fontColor: string, title: string, imagePath: string) {
		let slideBackground = {
			path: imagePath,
		};

		this.exportFile(slides, aspectRatio, fontColor, title, slideBackground);
	}

	public static exportWithImageData(slides: PreviewSlide[], aspectRatio: AspectRatios, fontColor: string, title: string, imageAsBase64: string) {
		let slideBackground = {
			data: imageAsBase64,
		};

		this.exportFile(slides, aspectRatio, fontColor, title, slideBackground);
	}

	private static mapEncodingToSpecialCharacter(symbol: string) {
		switch (symbol) {
			case "amp":
				return "&";
			case "lt":
				return "<";
			case "gt":
				return ">";
			case "#039":
				return "'";
			case "nbsp":
				return " ";
			default:
				return symbol;
		}
	}

	private static generateLineContent(
		line: SlideLine,
		lineContent: PptxGenJS.TextProps[],
		fontColor: string,
		blockQuoteFlag: boolean,
		orderedListCount: number,
		blockTagType: string
	) {
		line.lineChars.forEach((lineChar, index) => {
			const bulletChar = String.fromCharCode(8226);
			const tabChar = String.fromCharCode(9);
			let textOptions: PptxGenJS.TextPropsOptions = {
				color: fontColor,
			};
			let cleanedText = lineChar.char.replace(/&([^;]+);/g, (m, c) => this.mapEncodingToSpecialCharacter(c));

			if (lineChar.backgroundColor) textOptions.fill = { color: lineChar.backgroundColor };
			if (lineChar.fontFamily) textOptions.fontFace = lineChar.fontFamily;
			if (lineChar.isBold || blockTagType === "h1" || blockTagType === "h2") textOptions.bold = true;
			if (lineChar.isItalics || blockQuoteFlag) textOptions.italic = true;
			if (lineChar.isUnderlined) textOptions.underline = true;
			if (lineChar.backgroundColor) textOptions.color = lineChar.backgroundColor.replace("#", "");
			if (line.isUnorderedList && index === 0) {
				let bullet = bulletChar + tabChar;
				lineContent.push({ text: bullet, options: { color: fontColor } });
			}
			if (line.isOrderedList && index === 0) {
				let num = orderedListCount + "." + tabChar;
				lineContent.push({ text: num, options: { color: fontColor } });
				orderedListCount++;
			}

			if (blockTagType === "h1") {
				textOptions.fontSize = 68;
			} else if (blockTagType === "h2") {
				textOptions.fontSize = 56;
			} else {
				textOptions.fontSize = 42;
			}

			lineContent.push({ text: cleanedText, options: textOptions });

			if (index === line.lineChars.length - 1) {
				if (line.isOrderedList || line.isUnorderedList) {
					lineContent.push({ text: "\n", options: textOptions });
				}
			}
		});

		return lineContent;
	}

	private static addTextToSlide(presentation: PptxGenJS, previewSlides: PreviewSlide[], fontColor: string) {
		previewSlides.forEach((previewSlide) => {
			const lineWidth = 8.81;
			const lineHeight = 2.68;
			let xPosition = "5%";
			let yPosition = "25%";

			let blockQuoteFlag = false;
			let orderedListCount = 1;
			let slide = presentation.addSlide({ masterName: "MASTER_SLIDE", sectionTitle: "Test" });
			let lineContent: PptxGenJS.TextProps[] = [];
			let lineOptions: PptxGenJS.TextPropsOptions = {
				x: xPosition,
				y: yPosition,
				w: lineWidth,
				h: lineHeight,
			};

			previewSlide.lines.forEach((line) => {
				if (line.isBlockQuote) {
					blockQuoteFlag = true;
				}

				let blockTagType = line.blockTag ?? "p";
				lineContent = this.generateLineContent(line, lineContent, fontColor, blockQuoteFlag, orderedListCount, blockTagType);

				if (line.isOrderedList) {
					orderedListCount++;
				}

				if (line.textAlignment) lineOptions.align = line.textAlignment;
			});
			slide.addText(lineContent, lineOptions);
		});
		return presentation;
	}

	public static exportFile(previewSlides: PreviewSlide[], aspectRatio: AspectRatios, fontColor: string, title: string, slideBackground: any) {
		var presentation = new PptxGenJS();
		presentation.layout = aspectRatio === "16x9" ? "LAYOUT_16x9" : "LAYOUT_4x3";
		presentation.defineSlideMaster({
			title: "MASTER_SLIDE",
			background: slideBackground,
		});
		presentation = this.addTextToSlide(presentation, previewSlides, fontColor);
		presentation.writeFile(title);
	}
}
